import { memo, useCallback, useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { VoteDataDirection, type VoteDataDirectionType } from '@/query/vote';

import TextArea from '../ui/TextArea';

import { type BaseModalFCProps, type ModalFC } from './modalsManager';

export interface MeetingFeedbackModalProps extends BaseModalFCProps {
  onSubmit: (
    direction: VoteDataDirectionType,
    reasoning: string
  ) => Promise<void>;
  description?: string;
  initialDirection?: VoteDataDirectionType;
  title?: string;
}

export const MeetingFeedbackModal: ModalFC<MeetingFeedbackModalProps> = ({
  onSubmit,
  onOpenChange,
  open,
  description,
  initialDirection,
  title,
}) => {
  const [selectedDirection, setSelectedDirection] =
    useState<VoteDataDirectionType | null>(initialDirection ?? null);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedDirection(initialDirection ?? null);
    setFeedback('');
  }, [initialDirection]);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const handleSubmit = async () => {
    if (selectedDirection === null) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selectedDirection, feedback.trim());
      onOpenChange(false);
      resetForm();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
    resetForm();
  };

  const placeholder =
    selectedDirection === VoteDataDirection.UP
      ? 'What do you think went well?'
      : selectedDirection === VoteDataDirection.DOWN
        ? 'What do you think could be improved?'
        : 'Share your thoughts...';

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>{title ?? 'Send feedback'}</Dialog.Title>
          <Dialog.Description>
            {description ??
              'We review all feedback to improve your meeting notes experience'}
          </Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-4">
          <XStack className="gap-2">
            <VoteButton
              direction={VoteDataDirection.UP}
              isSelected={selectedDirection === VoteDataDirection.UP}
              onClick={() => setSelectedDirection(VoteDataDirection.UP)}
            />
            <VoteButton
              direction={VoteDataDirection.DOWN}
              isSelected={selectedDirection === VoteDataDirection.DOWN}
              onClick={() => setSelectedDirection(VoteDataDirection.DOWN)}
            />
          </XStack>

          <TextArea
            value={feedback}
            className="min-h-32"
            onChange={(event) => setFeedback(event.target.value)}
            placeholder={placeholder}
          />
        </YStack>

        <Dialog.Footer>
          <Button
            variant="secondary"
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={selectedDirection === null || isSubmitting}
            isLoading={isSubmitting}
          >
            Send feedback
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

interface VoteButtonProps {
  direction: VoteDataDirectionType;
  isSelected: boolean;
  onClick: () => void;
}

const VoteButton = memo(function VoteButton({
  direction,
  isSelected,
  onClick,
}: VoteButtonProps) {
  const isUp = direction === VoteDataDirection.UP;
  const Icon = isUp ? Icons.thumbsUp : Icons.thumbsDown;
  const label = isUp ? 'Good' : 'Bad';

  return (
    <Button
      variant={isSelected ? 'secondary' : 'outline'}
      size="sm"
      onClick={onClick}
      className={cn('flex-1 gap-2', isSelected && 'border border-transparent')}
    >
      <Icon className="size-4" />
      {label}
    </Button>
  );
});
